import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Loader2, Save } from 'lucide-react';
import { apiGetSettings, apiUpdateSettings, formatApiError } from '../../lib/api';
import { useSettings } from '../../contexts/SettingsContext';
import { toast } from 'sonner';

const sectionGroups = [
  {
    title: 'Social Links',
    description: 'Where visitors can find and follow you',
    fields: [
      { key: 'instagram_url', label: 'Instagram URL', placeholder: 'https://www.instagram.com/...' },
      { key: 'instagram_handle', label: 'Instagram Display Name', placeholder: '@yourhandle' },
      { key: 'linkedin_url', label: 'LinkedIn URL', placeholder: 'https://www.linkedin.com/in/...' },
      { key: 'linkedin_name', label: 'LinkedIn Display Name', placeholder: 'Your Full Name' },
    ],
  },
  {
    title: 'General',
    description: 'Email notifications and domain',
    fields: [
      { key: 'notification_email', label: 'Notification Email', placeholder: 'admin@example.com', help: 'Where contact form submissions are sent' },
      { key: 'domain', label: 'Domain', placeholder: 'yourdomain.com' },
    ],
  },
  {
    title: 'Hero Section',
    description: '"Where creators meet ambition."',
    fields: [
      { key: 'hero_eyebrow', label: 'Eyebrow Text', placeholder: 'Talent · Influence · Growth' },
      { key: 'hero_headline_line1', label: 'Headline (start)', placeholder: 'Where' },
      { key: 'hero_headline_italic', label: 'Headline (italic accent)', placeholder: 'creators' },
      { key: 'hero_headline_line2', label: 'Headline (end)', placeholder: 'meet ambition.' },
      { key: 'hero_subheading', label: 'Subheading', type: 'textarea' },
      { key: 'hero_cta_primary', label: 'Primary CTA Text', placeholder: 'Start a campaign' },
      { key: 'hero_cta_secondary', label: 'Secondary CTA Text', placeholder: 'Meet our creators' },
    ],
  },
  {
    title: 'About Section',
    description: '"Built for brands. Loved by creators."',
    fields: [
      { key: 'about_eyebrow', label: 'Eyebrow Text', placeholder: 'What we do' },
      { key: 'about_headline_line1', label: 'Headline (line 1)', placeholder: 'Built for brands.' },
      { key: 'about_headline_italic', label: 'Italic accent', placeholder: 'Loved' },
      { key: 'about_headline_line2', label: 'Headline (continued)', placeholder: 'by creators.' },
      { key: 'about_description', label: 'Description', type: 'textarea' },
    ],
  },
  {
    title: 'Creators Section',
    description: '"Voices that move the needle."',
    fields: [
      { key: 'creators_eyebrow', label: 'Eyebrow Text', placeholder: 'Our Exclusive Roster' },
      { key: 'creators_headline_line1', label: 'Headline (start)', placeholder: 'Voices that' },
      { key: 'creators_headline_italic', label: 'Italic accent', placeholder: 'move' },
      { key: 'creators_headline_line2', label: 'Headline (end)', placeholder: 'the needle.' },
    ],
  },
  {
    title: 'Brands Section',
    description: '"Brands that trust us with their stories"',
    fields: [
      { key: 'brands_eyebrow', label: 'Eyebrow Text', placeholder: 'Trusted Partners' },
      { key: 'brands_headline_line1', label: 'Headline (start)', placeholder: 'Brands that trust us with their' },
      { key: 'brands_headline_italic', label: 'Italic accent', placeholder: 'stories' },
    ],
  },
  {
    title: 'Contact Section',
    description: '"Got a brand? Got a story?"',
    fields: [
      { key: 'contact_eyebrow', label: 'Eyebrow Text', placeholder: "Let's talk" },
      { key: 'contact_headline_line1', label: 'Headline (line 1)', placeholder: 'Got a brand?' },
      { key: 'contact_headline_line2', label: 'Headline (line 2)', placeholder: 'Got a story?' },
      { key: 'contact_headline_italic', label: 'Italic closing line', placeholder: "We've got the rest." },
      { key: 'contact_description', label: 'Description', type: 'textarea' },
    ],
  },
  {
    title: 'Footer',
    description: 'Footer copy',
    fields: [
      { key: 'footer_description', label: 'Footer Description', type: 'textarea' },
    ],
  },
];

export default function SettingsAdmin() {
  const { refresh } = useSettings();
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiGetSettings();
      setForm(data);
    } catch (e) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleChange = (key, value) => {
    setForm({ ...form, [key]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await apiUpdateSettings(form);
      await refresh();
      toast.success('Settings saved successfully — changes are live!');
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Failed to save settings');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="animate-spin text-orange-600" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-admin">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Site Settings</h2>
          <p className="text-gray-500">Edit copy and links across the public website</p>
        </div>
        <Button
          onClick={handleSubmit}
          disabled={submitting}
          className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white font-bold"
          data-testid="settings-save-button"
        >
          {submitting ? (
            <>
              <Loader2 size={16} className="mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save size={16} className="mr-2" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {sectionGroups.map((group) => (
          <Card key={group.title} className="border-2 border-gray-100">
            <CardHeader>
              <CardTitle className="text-lg">{group.title}</CardTitle>
              <CardDescription>{group.description}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {group.fields.map((field) => (
                <div key={field.key} className="space-y-1.5">
                  <Label htmlFor={field.key} className="text-sm font-semibold text-gray-700">
                    {field.label}
                  </Label>
                  {field.type === 'textarea' ? (
                    <Textarea
                      id={field.key}
                      data-testid={`settings-${field.key}`}
                      value={form[field.key] || ''}
                      onChange={(e) => handleChange(field.key, e.target.value)}
                      rows={3}
                    />
                  ) : (
                    <Input
                      id={field.key}
                      data-testid={`settings-${field.key}`}
                      value={form[field.key] || ''}
                      onChange={(e) => handleChange(field.key, e.target.value)}
                      placeholder={field.placeholder}
                    />
                  )}
                  {field.help && (
                    <p className="text-xs text-gray-500">{field.help}</p>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        ))}

        <div className="flex justify-end pt-4">
          <Button
            type="submit"
            disabled={submitting}
            className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white font-bold px-8"
            data-testid="settings-save-button-bottom"
          >
            {submitting ? (
              <>
                <Loader2 size={16} className="mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save size={16} className="mr-2" />
                Save All Changes
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
